import React, { useEffect, useMemo, useState } from "react";
import {
  LayoutDashboard,
  Users,
  User,
  UserRound,
  TrendingUp,
  TrendingDown,
  Briefcase,
  RefreshCw,
} from "lucide-react";
import { computeEmployeeDashboardMetrics } from "../../lib/employeeDashboardMetrics";

function MetricCard({ darkMode, label, value, sub, icon: Icon, tone = "default" }) {
  const toneClass =
    tone === "up"
      ? darkMode
        ? "text-emerald-400"
        : "text-emerald-700"
      : tone === "down"
      ? darkMode
        ? "text-rose-400"
        : "text-rose-700"
      : darkMode
      ? "text-gray-100"
      : "text-gray-900";

  return (
    <div
      className={`rounded-lg border p-3 ${
        darkMode
          ? "border-[#3C3C4C] bg-[#242424]"
          : "border-gray-200 bg-gray-50/90 shadow-sm"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {label}
          </div>
          <div className={`mt-1 text-xl font-bold leading-tight ${toneClass}`}>
            {value}
          </div>
          {sub ? (
            <div className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">
              {sub}
            </div>
          ) : null}
        </div>
        {Icon ? (
          <Icon
            className={`h-4 w-4 shrink-0 opacity-70 ${
              darkMode ? "text-redhalo-mint" : "text-redhalo-navy"
            }`}
            aria-hidden
          />
        ) : null}
      </div>
    </div>
  );
}

export default function EmployeeDashboardPanel({
  darkMode = false,
  isFlipped = false,
  onFlip,
  showFlipButton = true,
  employees = [],
  loading = false,
}) {
  const now = new Date();
  const [periodMode, setPeriodMode] = useState("month");
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [rangeFrom, setRangeFrom] = useState("");
  const [rangeTo, setRangeTo] = useState("");

  useEffect(() => {
    if (periodMode !== "range") return;
    if (rangeFrom && rangeTo) return;
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const lastDay = new Date(y, now.getMonth() + 1, 0).getDate();
    if (!rangeFrom) setRangeFrom(`01-${m}-${y}`);
    if (!rangeTo) setRangeTo(`${lastDay}-${m}-${y}`);
  }, [periodMode, rangeFrom, rangeTo, now]);

  const metrics = useMemo(
    () =>
      computeEmployeeDashboardMetrics(employees, {
        periodMode,
        month,
        year,
        rangeFrom,
        rangeTo,
      }),
    [employees, periodMode, month, year, rangeFrom, rangeTo]
  );

  const yearOptions = useMemo(() => {
    const current = now.getFullYear();
    return Array.from({ length: 8 }, (_, i) => current - i);
  }, [now]);

  const selectClass = darkMode
    ? "rounded-md border border-[#3C3C4C] bg-[#2C2C2C] px-2 py-1.5 text-xs text-[#E0E0E0] focus:border-redhalo-red focus:outline-none"
    : "rounded-md border border-[#f3d6bf] bg-white px-2 py-1.5 text-xs text-gray-900 focus:border-[#ef6f2e] focus:outline-none";

  const inputClass = darkMode
    ? "rounded-md border border-[#3C3C4C] bg-[#2C2C2C] px-2 py-1.5 text-xs text-[#E0E0E0] focus:border-redhalo-red focus:outline-none"
    : "rounded-md border border-[#f3d6bf] bg-white px-2 py-1.5 text-xs text-gray-900 focus:border-[#ef6f2e] focus:outline-none";

  return (
    <>
      {showFlipButton && (
        <button
          type="button"
          onClick={onFlip}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full border backdrop-blur-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-redhalo-red/50 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-[#121212] ${
            darkMode
              ? "border-redhalo-red/70 bg-redhalo-navy/40 text-redhalo-mint hover:bg-redhalo-navy/60"
              : "border-redhalo-red bg-redhalo-mint/40 text-redhalo-navy-dark hover:bg-redhalo-mint/60"
          }`}
          title={isFlipped ? "Back to employee list" : "Flip to workforce dashboard"}
        >
          <LayoutDashboard className="w-3.5 h-3.5" />
          {isFlipped ? "Back" : "Flip Dashboard"}
        </button>
      )}

      {isFlipped && (
        <div
          className={`min-h-0 flex-1 overflow-y-auto rounded-lg border ${
            darkMode
              ? "border-[#3C3C4C] bg-[#1D1D1D]"
              : "border-gray-200 bg-white"
          }`}
        >
          <div className="px-4 py-4">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <div>
                <h3
                  className={`text-lg font-bold tracking-tight ${
                    darkMode ? "text-gray-100" : "text-gray-900"
                  }`}
                >
                  Workforce Dashboard
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Period: {metrics.periodLabel}
                  {loading ? " · Updating…" : ""}
                </p>
              </div>
            </div>

            <div
              className={`mb-4 rounded-md border p-3 ${
                darkMode ? "border-[#3C3C4C] bg-[#1C1C1C]" : "border-[#165b5d]/30 bg-white"
              }`}
            >
              <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                Period
              </div>
              <div className="flex flex-wrap items-end gap-2">
                {[
                  { id: "month", label: "Month" },
                  { id: "year", label: "Year" },
                  { id: "range", label: "Range" },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setPeriodMode(tab.id)}
                    className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                      periodMode === tab.id
                        ? darkMode
                          ? "bg-redhalo-navy text-redhalo-mint"
                          : "bg-redhalo-navy text-white"
                        : darkMode
                        ? "bg-[#2C2C2C] text-gray-300 hover:bg-[#333]"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}

                {periodMode === "month" && (
                  <>
                    <select
                      className={selectClass}
                      value={month}
                      onChange={(e) => setMonth(Number(e.target.value))}
                      aria-label="Month"
                    >
                      {Array.from({ length: 12 }, (_, i) => (
                        <option key={i + 1} value={i + 1}>
                          {new Date(2000, i, 1).toLocaleString("default", {
                            month: "long",
                          })}
                        </option>
                      ))}
                    </select>
                    <select
                      className={selectClass}
                      value={year}
                      onChange={(e) => setYear(Number(e.target.value))}
                      aria-label="Year"
                    >
                      {yearOptions.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </>
                )}

                {periodMode === "year" && (
                  <select
                    className={selectClass}
                    value={year}
                    onChange={(e) => setYear(Number(e.target.value))}
                    aria-label="Year"
                  >
                    {yearOptions.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                )}

                {periodMode === "range" && (
                  <>
                    <label className="flex flex-col gap-0.5 text-[10px] text-gray-500">
                      From (DD-MM-YYYY)
                      <input
                        type="text"
                        className={inputClass}
                        value={rangeFrom}
                        onChange={(e) => setRangeFrom(e.target.value)}
                        placeholder="01-01-2026"
                      />
                    </label>
                    <label className="flex flex-col gap-0.5 text-[10px] text-gray-500">
                      To (DD-MM-YYYY)
                      <input
                        type="text"
                        className={inputClass}
                        value={rangeTo}
                        onChange={(e) => setRangeTo(e.target.value)}
                        placeholder="31-01-2026"
                      />
                    </label>
                  </>
                )}
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-16">
                <RefreshCw className="h-8 w-8 animate-spin text-redhalo-red" />
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                <MetricCard
                  darkMode={darkMode}
                  label="Headcount"
                  value={metrics.headcount}
                  sub="All records in view"
                  icon={Users}
                />
                <MetricCard
                  darkMode={darkMode}
                  label="Active"
                  value={metrics.active}
                  sub="Excl. relieved / resigned / terminated"
                  icon={Briefcase}
                />
                <MetricCard
                  darkMode={darkMode}
                  label="Men"
                  value={metrics.male}
                  sub={
                    metrics.male + metrics.female > 0
                      ? `${metrics.malePct}% of known gender`
                      : "No gender data"
                  }
                  icon={User}
                />
                <MetricCard
                  darkMode={darkMode}
                  label="Women"
                  value={metrics.female}
                  sub={
                    metrics.male + metrics.female > 0
                      ? `${metrics.femalePct}% of known gender`
                      : "No gender data"
                  }
                  icon={UserRound}
                />
                <MetricCard
                  darkMode={darkMode}
                  label="M : F ratio"
                  value={metrics.genderRatio}
                  sub={`Other / unknown: ${metrics.otherGender}`}
                />
                <MetricCard
                  darkMode={darkMode}
                  label="New joinees"
                  value={metrics.newJoinees}
                  sub="Joined in selected period"
                  icon={TrendingUp}
                  tone="up"
                />
                <MetricCard
                  darkMode={darkMode}
                  label="New joinee rate"
                  value={`${metrics.newJoineeRate}%`}
                  sub="Vs active headcount"
                  tone="up"
                />
                <MetricCard
                  darkMode={darkMode}
                  label="Attrition"
                  value={metrics.attrition}
                  sub="Exits in selected period"
                  icon={TrendingDown}
                  tone="down"
                />
                <MetricCard
                  darkMode={darkMode}
                  label="Attrition rate"
                  value={`${metrics.attritionRate}%`}
                  sub="Vs active headcount"
                  tone="down"
                />
                <MetricCard
                  darkMode={darkMode}
                  label="Probation"
                  value={metrics.probation}
                />
                <MetricCard
                  darkMode={darkMode}
                  label="Confirmed"
                  value={metrics.confirmed}
                />
                <MetricCard
                  darkMode={darkMode}
                  label="Full-time"
                  value={metrics.fullTime}
                />
                <MetricCard
                  darkMode={darkMode}
                  label="External"
                  value={metrics.external}
                />
                <MetricCard
                  darkMode={darkMode}
                  label="Relieved"
                  value={metrics.relieved}
                />
                <MetricCard
                  darkMode={darkMode}
                  label="Resigned"
                  value={metrics.resigned}
                />
                <MetricCard
                  darkMode={darkMode}
                  label="Terminated"
                  value={metrics.terminated}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
